// The frame-time distribution (P0.12) — Architecture §6.3's raw material.
//
// Parked at P0.8: "the FPS readout polls; the quality governor will need per-frame times… P0.12's
// bench harness wants the same thing. Worth designing once, there." So this is written for both
// callers, and only one of them exists yet. §6.3's controller "measures the real budget (8.3 ms
// target) and steps declared quality tiers… with hysteresis" — hysteresis needs a distribution to
// apply against, because the whole point is to not react to one bad frame.
//
// **P2.10 moved the arithmetic out from under this file** into `platform/sab/histogram.ts`. Not a
// tidy-up: the latency probe needed the same maths, and the audio thread may not import from
// /visual — `.dependency-cruiser.cjs` whitelists /platform/sab for the worklet and refuses the
// rest. Nothing about the frame side changed. Every signature here is the one P0.12 shipped, the
// spec is bound rather than passed, and the callers below never learned there was a spec at all.
//
// What stays here is what is genuinely about frames: which histogram bases exist (they live in
// /platform/types with the rest of the SAB contracts, because the render worker and the main
// thread must agree on them) and `fpsPercentile`, whose reciprocal only makes sense for frames.
//
// /visual/core rather than /platform, because §11 puts the quality governor here and this is the
// number it governs on.

import {
  bucketFor as bucketForSpec,
  clearHistogram as clearHistogramSpec,
  countSlowerThan as countSlowerThanSpec,
  overflowCount as overflowCountSpec,
  percentileMicros as percentileMicrosSpec,
  readDistribution as readDistributionSpec,
  recordSample as recordSampleSpec,
  totalCount as totalCountSpec,
  type DistributionMs,
} from '../../platform/sab/histogram';
import { FRAME_HISTOGRAM } from '../../platform/types/render';

/** Which bucket a frame duration lands in. See `histogram.ts` for the clamping. */
export function bucketFor(micros: number): number {
  return bucketForSpec(FRAME_HISTOGRAM, micros);
}

/** Record one frame. One `Atomics.add`, on the frame budget, from whichever thread renders. */
export function recordSample(
  histogram: Int32Array,
  baseI32: number,
  micros: number,
): void {
  recordSampleSpec(FRAME_HISTOGRAM, histogram, baseI32, micros);
}

export function totalCount(histogram: Int32Array, baseI32: number): number {
  return totalCountSpec(FRAME_HISTOGRAM, histogram, baseI32);
}

/** The q-th percentile as a bucket's **upper** edge; null when nothing was measured. */
export function percentileMicros(
  histogram: Int32Array,
  baseI32: number,
  quantile: number,
): number | null {
  return percentileMicrosSpec(FRAME_HISTOGRAM, histogram, baseI32, quantile);
}

/** Frames **strictly slower** than a threshold — "how many missed 16.7 ms" (NFR-P3). */
export function countSlowerThan(
  histogram: Int32Array,
  baseI32: number,
  micros: number,
): number {
  return countSlowerThanSpec(FRAME_HISTOGRAM, histogram, baseI32, micros);
}

/** Frames at or beyond the last bucket (≥63.75 ms), where the histogram stopped resolving. */
export function overflowCount(histogram: Int32Array, baseI32: number): number {
  return overflowCountSpec(FRAME_HISTOGRAM, histogram, baseI32);
}

export function clearHistogram(histogram: Int32Array, baseI32: number): void {
  clearHistogramSpec(FRAME_HISTOGRAM, histogram, baseI32);
}

/**
 * A frame-time distribution, in the units a human reads and a budget is written in.
 *
 * An alias rather than a second declaration of the same eight fields: two structurally identical
 * interfaces that must stay identical are a divergence waiting to happen, and the name is what the
 * frame side's callers actually read.
 */
export type FrameDistributionMs = DistributionMs;

export function readDistribution(
  histogram: Int32Array,
  baseI32: number,
): FrameDistributionMs {
  return readDistributionSpec(FRAME_HISTOGRAM, histogram, baseI32);
}

/**
 * FPS from a frame-interval percentile.
 *
 * "p90 FPS ≥55" (the release guardrail, PRD §10) means **90% of frames render at least that fast**.
 * A frame is at least X fast exactly when its interval is at most 1/X — so the 90% of frames that
 * meet the bar are the 90% with the *shortest* intervals, and the number quoted is the p90 of
 * interval, reciprocated. The quantile passes straight through; the reciprocal does the flipping.
 *
 * This function had `1 - quantile` in it when it was written, on the reasoning that "a long
 * interval is a low frame rate, so p90 FPS is the p10 of interval" — which sounds right, is wrong,
 * and would have reported the *fastest* frames as the slow tail. Every FPS budget in the report
 * would then have passed on a machine visibly dropping frames.
 *
 * It was caught by the test written to catch it, which is the only reason it is a comment rather
 * than a shipped bug. Worth remembering for the shape: a plausible sentence about an inversion is
 * not a proof, and this is the same lesson P0.7 learned about `prefer-for-of` — the justification
 * that "sounded right and was wrong" is the dangerous kind, because it gets cited.
 */
export function fpsPercentile(
  histogram: Int32Array,
  baseI32: number,
  quantile: number,
): number | null {
  const micros = percentileMicros(histogram, baseI32, quantile);
  if (micros === null || micros <= 0) {
    return null;
  }
  return 1_000_000 / micros;
}
